import React, { useMemo, useState } from "react";
import { Table, Button, Input } from "antd";
import { DeleteOutlined } from "@ant-design/icons";

import CategorySelect from "../pages/TransactionView/CategorySelect";
import VatCategorySelect from "../pages/TransactionView/VatCategorySelect";
import {
  TransactionSplit,
  DepreciableCategory,
  ITransaction,
} from "../../types";
import BusinessAsset from "./BusinessAssetContainer";

import SplitAmountInput from "./SplitAmountInput";
import {
  DEPRECIABLE_CATEGORY_CODES,
  LATEST_POSSIBLE_PURCHASE_DATE,
} from "../BusinessAsset/constants";
import { BusinessAssetType } from "../../types";
import {
  BUSINESS_ASSET_CATEGORIES,
  EXTERNAL_TRANSACTION_CATEGORIES,
} from "../../utils";
import { isDepreciableCategoryCode } from "../BusinessAsset/utils";
import TransactionSubmissionStatus from "../pages/VerificationView/TransactionList/TransactionSubmissionStatus";
import {
  isInvalidRecipientVatIdForVatCategory,
  showsRecipientVatIdField,
} from "../pages/TransactionView/helpers";

/** Column widths (px) — sum drives `scroll.x` so the table keeps column sizes and scrolls horizontally in narrow layouts. */
const SPLIT_COL_WIDTH = {
  amount: 120,
  category: 340,
  vatCategory: 325,
  recipientVatId: 170,
  submitted: 150,
  delete: 56,
  rowExpand: 48,
} as const;

export const getResetAssetFields = (
  categoryCode: string
): {
  assetType: BusinessAssetType;
  assetClass: string;
  assetClassCustom: string;
  purchaseDate: string;
  depreciationYears: string;
  depreciationYearsEditable: boolean;
} => ({
  assetType: BusinessAssetType.MOVABLE_OTHERS,
  assetClass: "",
  assetClassCustom: "",
  purchaseDate: LATEST_POSSIBLE_PURCHASE_DATE,
  // If category is OVER_250, asset depreciates immediately, hence 0
  depreciationYears: categoryCode === DepreciableCategory.OVER_250 ? "0" : "",
  depreciationYearsEditable: categoryCode === DepreciableCategory.OVER_800,
});

const TransactionSplitView = ({
  splits,
  transaction,
  isEscalateClicked,
  isNextClicked,
  disableBusinessAssetFeatures,
  onChangeHandler,
  onDeleteHandler,
  isTransactionVerified,
}: {
  splits: Array<Partial<TransactionSplit>>;
  transaction: Pick<
    ITransaction,
    "amount" | "businessAssets" | "vatYearPaymentFrequency" | "currency"
  >;
  isEscalateClicked?: boolean;
  isNextClicked?: boolean;
  disableBusinessAssetFeatures?: boolean;
  onChangeHandler: (split: Partial<TransactionSplit>, index: number) => void;
  onDeleteHandler: (index: number) => void;
  isTransactionVerified?: boolean;
}) => {
  const [expandedRowKeys, setExpandedRowKeys] = useState<number[]>([]);

  const showRecipientVatIdColumn = splits.some((s) =>
    showsRecipientVatIdField(s.vatCategoryCode)
  );

  const tableScrollMinWidth = useMemo(() => {
    let sum =
      SPLIT_COL_WIDTH.amount +
      SPLIT_COL_WIDTH.category +
      SPLIT_COL_WIDTH.vatCategory +
      SPLIT_COL_WIDTH.submitted;
    if (showRecipientVatIdColumn) {
      sum += SPLIT_COL_WIDTH.recipientVatId;
    }
    if (splits.length > 2) {
      sum += SPLIT_COL_WIDTH.delete;
    }
    if (!disableBusinessAssetFeatures) {
      sum += SPLIT_COL_WIDTH.rowExpand;
    }
    return sum;
  }, [showRecipientVatIdColumn, splits.length, disableBusinessAssetFeatures]);

  const columns = useMemo(
    () => [
      {
        title: "Amount",
        key: "amount",
        width: SPLIT_COL_WIDTH.amount,
        render: (
          record: Partial<TransactionSplit>,
          text: any,
          index: number
        ) => (
          <SplitAmountInput
            split={record}
            index={index}
            onChangeHandler={onChangeHandler}
            disabled={isTransactionVerified}
            currency={transaction.currency}
          />
        ),
      },
      {
        title: "Category",
        key: "categoryCode",
        width: SPLIT_COL_WIDTH.category,
        render: (
          record: Partial<TransactionSplit>,
          text: any,
          index: number
        ) => {
          return (
            <CategorySelect
              id={`category-${index}`}
              value={record?.categoryCode}
              disabled={isTransactionVerified}
              transactionAmount={transaction.amount}
              invalid={isNextClicked && !record?.categoryCode}
              onChangeHandler={(categoryCode: any) =>
                onChangeHandler(
                  {
                    ...record,
                    categoryCode,
                    businessAssetForm: undefined,
                    // If depreciable category code is selected,
                    // reset business asset form fields to their default values
                    ...(DEPRECIABLE_CATEGORY_CODES.includes(categoryCode) && {
                      businessAssetForm: getResetAssetFields(categoryCode),
                    }),
                  },
                  index
                )
              }
              style={{ width: "100%", maxWidth: "320px" }}
              disabledOptions={[
                ...EXTERNAL_TRANSACTION_CATEGORIES,
                ...(disableBusinessAssetFeatures
                  ? BUSINESS_ASSET_CATEGORIES
                  : []),
              ]}
            />
          );
        },
      },
      {
        title: "VAT Category",
        key: "vatCategoryCode",
        width: SPLIT_COL_WIDTH.vatCategory,
        render: (
          record: Partial<TransactionSplit>,
          text: any,
          index: number
        ) => {
          return (
            <VatCategorySelect
              id={`vat-category-${index}`}
              value={record?.vatCategoryCode}
              disabled={isTransactionVerified}
              invalid={isNextClicked && !record?.vatCategoryCode}
              onChangeHandler={(vatCategoryCode) =>
                onChangeHandler(
                  {
                    ...record,
                    vatCategoryCode,
                    ...(!showsRecipientVatIdField(vatCategoryCode) && {
                      recipientVatId: undefined,
                    }),
                  },
                  index
                )
              }
              updateValueOnCategoryChange
              kontaxCategory={record.categoryCode}
              vatYearPaymentFrequency={transaction.vatYearPaymentFrequency}
              style={{ width: "100%", maxWidth: "305px" }}
            />
          );
        },
      },
      ...(showRecipientVatIdColumn
        ? [
            {
              title: "Recipient VAT ID",
              key: "recipientVatId",
              width: SPLIT_COL_WIDTH.recipientVatId,
              render: (
                record: Partial<TransactionSplit>,
                _text: unknown,
                index: number
              ) =>
                showsRecipientVatIdField(record.vatCategoryCode) ? (
                  <div
                    style={{
                      paddingRight: 24,
                      boxSizing: "border-box",
                    }}
                  >
                    <Input
                      id={`recipient-vat-id-${index}`}
                      data-test={`split-recipient-vat-id-${index}`}
                      value={record.recipientVatId ?? ""}
                      disabled={isTransactionVerified}
                      status={
                        isInvalidRecipientVatIdForVatCategory(
                          record.vatCategoryCode,
                          record.recipientVatId
                        )
                          ? "error"
                          : undefined
                      }
                      onChange={(e) =>
                        onChangeHandler(
                          { ...record, recipientVatId: e.target.value.trim() },
                          index
                        )
                      }
                      style={{ width: "100%" }}
                    />
                  </div>
                ) : (
                  <div
                    style={{
                      minHeight: 32,
                      paddingRight: 24,
                      boxSizing: "border-box",
                    }}
                  />
                ),
            },
          ]
        : []),
      {
        title: "Submitted",
        key: "submitted",
        width: SPLIT_COL_WIDTH.submitted,
        render: (record: Partial<TransactionSplit>) => (
          <TransactionSubmissionStatus record={record} />
        ),
        showSorterTooltip: false,
        onCell: () => ({
          style: {
            verticalAlign: "middle",
          },
        }),
      },
      ...(splits.length > 2
        ? [
            {
              title: "",
              key: "splitDelete",
              width: SPLIT_COL_WIDTH.delete,
              render: (
                _record: Partial<TransactionSplit>,
                _text: unknown,
                index: number
              ) => (
                <Button
                  shape="circle"
                  icon={<DeleteOutlined />}
                  onClick={() => onDeleteHandler(index)}
                />
              ),
            },
          ]
        : []),
    ],
    [
      disableBusinessAssetFeatures,
      isNextClicked,
      isTransactionVerified,
      onChangeHandler,
      onDeleteHandler,
      showRecipientVatIdColumn,
      splits.length,
      transaction.amount,
      transaction.currency,
      transaction.vatYearPaymentFrequency,
    ]
  );

  const renderExpandedRow = (
    split: Partial<TransactionSplit>,
    index: number
  ) => {
    const onExpandedRowChange = (data: Partial<TransactionSplit>) => {
      onChangeHandler(data, index);
    };

    return (
      <div>
        {!disableBusinessAssetFeatures &&
          split &&
          split.categoryCode &&
          DEPRECIABLE_CATEGORY_CODES.includes(split.categoryCode) && (
            <BusinessAsset
              record={split}
              onChange={onExpandedRowChange}
              isSubmitClicked={Boolean(isNextClicked || isEscalateClicked)}
            />
          )}
      </div>
    );
  };

  const onExpand = async (expanded: boolean, record: any) => {
    const keys = expanded
      ? [...expandedRowKeys, record.key]
      : expandedRowKeys.filter((key) => key !== record.key);
    setExpandedRowKeys(keys);
  };

  const data = splits.map((split, index) => ({
    key: index,
    ...split,
  }));

  const expandable = disableBusinessAssetFeatures
    ? undefined
    : {
        expandedRowRender: renderExpandedRow,
        expandedRowClassName: () => "expandable-row",
        expandedRowKeys,
        onExpand,
        rowExpandable: (record: Partial<TransactionSplit>) =>
          isDepreciableCategoryCode(record.categoryCode) &&
          !transaction.businessAssets?.some(
            ({ businessAssetableId }) => businessAssetableId === record.uuid
          ),
      };

  return (
    <div style={{ maxWidth: "100%", minWidth: 0, width: "100%" }}>
      <Table
        data-test="splitTable"
        columns={columns}
        dataSource={data}
        pagination={false}
        scroll={{ x: tableScrollMinWidth }}
        tableLayout="fixed"
        size="middle"
        expandable={expandable}
      />
    </div>
  );
};

export default TransactionSplitView;
